import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridFilteredDescendantCountLookupSelector,
  useGridApiRef,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { Gantt } from 'ju-gantt-task-react-frozen';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { getCookie } from 'common/utils/cookies';
import { useAppSelector } from 'components/app/redux/store';

import {
  clearGanttSave,
  getaGanttIssueTrackers,
  getaGanttIssueTrackersAssignees,
  getGantCsv,
  getGantFilters,
  getGanttMovingIssue,
  getGanttPermissions,
  getIssuePredecessors,
  saveGanttSettings,
} from '../../../../packages/common/api';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import CheckedRadioIcon from '../../../../packages/common/shared-ui/src/icons/CheckedRadioIcon';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import UncheckedRadioIcon from '../../../../packages/common/shared-ui/src/icons/UncheckedRadioIcon';
import ZoomCancelIcon from '../../../../packages/common/shared-ui/src/icons/ZoomCancelIcon';
import ZoomInIcon from '../../../../packages/common/shared-ui/src/icons/ZoomInIcon';
import ZoomOutIcon from '../../../../packages/common/shared-ui/src/icons/ZoomOutIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import {
  formatFilters,
  paramsToQuery,
  queryToParamsGant,
  selectFilters,
} from '../../../../packages/common/utils/gridFiltersHelperNew';
import { LanguageMap } from '../../../../packages/common/utils/languageMap';
import CommonPage from '../../CommonPage/CommonPage';
import GridFiltersModalNew from '../../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../../GridFiltersPanelNew/GridFiltersPanelNew';
import { store } from '../../app/redux';
import { useWebSockets } from '../../resourcePlan/useWebsockets';
import GanttAddBasePlanModal from '../components/GanttAddBasePlanModal/GanttAddBasePlanModal';
import GanttEditCellUser from '../components/GanttEditCellUser/GanttEditCellUser';
import GanttIssueModal from '../components/GanttIssueModal/GanttIssueModal';
import GanttIssueRelatedModal from '../components/GanttIssueRelatedModal/GanttIssueRelatedModal';

import 'ju-gantt-task-react-frozen/dist/style.css';

// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));

// Original class: ganttHolder
const StyledGanttHolder = styled(Grid)(theme => ({
  overflow: 'auto',
  overflowX: 'hidden',
  borderRadius: '16px',
  boxSizing: 'border-box',
  display: 'flex',
  flexFlow: 'wrap',
  width: '100%',
  outline: 'none',

  '&::-webkit-scrollbar': {
    height: '6px',
    width: '6px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },

  '& .gantt': {
    borderRadius: 0,
    '& ._ganttVerticalContainer_kayy3_1': {
      '& .ganttHeaderSvg': {
        '& ._calendarHeader_fydlr_31': {
          stroke: 'none',
        },
      },
    },
  },
}));
// Original class: rowAutocomlpete
const StyledRowAutocomlpete = styled(Autocomplete, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
  },

  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '0',
    lineHeight: '20px',
    height: 'auto',
    ...(changedStyle && {
      '& .MuiAutocomplete-input': {
        color: '#212346',
      },
    }),
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
      height: 'auto',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    transform: 'none',
    '&.Mui-focused': {
      transform: 'none',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '0',
    color: '#7174AC',
    ...(changedStyle && {
      color: '#212346',
    }),
    '&.changed': {
      color: '#212346',
    },
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      ...(changedStyle && {
        color: '#212346',
      }),
      padding: 0,
      '&::placeholder': {
        color: '#7174AC',
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: 0,
      color: '#7174AC',
      ...(changedStyle && {
        color: '#212346',
      }),
      fontSize: '12px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro, {
  shouldForwardProp: prop => !['fixedHeaderStyle'].includes(prop),
})(({ theme, fixedHeaderStyle }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    ...(fixedHeaderStyle && {
      position: 'static',
    }),

    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      ...(fixedHeaderStyle && {
        backgroundColor: '#fff',
        zIndex: 1,
        position: 'absolute',
      }),
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',

            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',

          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
      ...(fixedHeaderStyle && {
        paddingTop: '75px',
      }),
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '--height': 'auto!important',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'hidden',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '##DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
      display: 'flex',
      alignItems: 'center',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      backgroundColor: '#fff',
      ...(fixedHeaderStyle
        ? { position: 'static', borderRadius: 0 }
        : { borderRadius: '16px' }),
    },
  },
  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const GanttContainer = () => {
  const featureFlags = useAppSelector(state => state.configuration.features);
  const getLocalString = useTranslation();
  const ViewModeEnum = useMemo(() => {
    return ['Hour', 'Half Day', 'Day', 'Week', 'Month', 'Year'];
  }, []);
  const expansionState = React.useRef({});
  const [rowsToUpdate, setRowsToUpdate] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [firstLoadGroup, setFirstLoadGroup] = useState(true);
  const [open, setOpen] = useState(false);
  const [rightGantState, setRightGantState] = useState(1);
  const [openGantIssueModal, setOpenGantIssueModal] = useState(false);
  const [openAddBasePlanModal, setOpenAddBasePlanModal] = useState(false);
  const [openGantIssueRelatedModal, setOpenGantIssueRelatedModal] =
    useState(false);
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  //const [query, setQuery] = useQueryParams({
  //  project: '',
  //  filters: ArrayParam,
  //});
  const formatDate = useCallback(dateStr => {
    if (dateStr) {
      return dayjs(dateStr).format('DD.MM.YYYY');
    }
    return dateStr;
  }, []);
  const apiRef = useGridApiRef();
  const dataGridProRef = useRef(null);
  const accessToken = getCookie('access_token');
  const pageTitle = getLocalString('GanttContainer', 'title');
  const expansionLookup = useRef({});
  const [ganttRightSideData, setGanttRightSideData] = useState([]);
  const [ganttTableData, setGanttTableData] = useState([]);
  const [ganttTableShowChilds, setGanttTableShowChilds] = useState(true);
  const [ganttHideTask, setGanttHideTask] = useState();
  const [ganttTableZoom, setGanttTableZoom] = useState();
  const [ganttTableRowData, setGanttTableRowData] = useState([]);
  const [ganttTableColumnsData, setGanttTableColumnsData] = useState([]);
  const [ganttTableViewMode, setGanttTableViewMode] = useState(0);
  const [ganttTableViewModeEnum, setGanttTableViewModeEnum] = useState(
    ViewModeEnum['0'],
  );
  const [ganttModalData, setGanttModalData] = useState(null);

  const [selectedRow, setSelectedRow] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editIssueData, setEditIssueData] = useState(null);
  const [contextSubMenu, setContextSubMenu] = useState(null);
  const [movingIssueOptions, setMovingIssueOptions] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [rowUsers, setRowUsers] = useState(null);
  const [predecessors, setPredecessors] = useState([]);
  const [showSavingError, setShowSavingError] = useState({
    state: false,
    errors: [],
  });
  const [gantErrorValue, setGanttErrorValue] = useState('save_all');
  const [ganttSavingLoader, setGanttSavingLoader] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [ganttRowsErrors, setGanttRowsErrors] = useState([]);
  const [ganttStartDateErrors, setGanttStartDateErrors] = useState(false);
  const [ganttDateErrors, setGanttDateErrors] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [tempCellValue, setTempCellValue] = useState([]);
  const socket = useWebSockets();
  const state = store.getState();
  const pageUseParams = useParams();
  const [language, setLanguage] = useState('ru');
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [query, setQuery] = useQueryParams({
    filters: StringParam,
    basePlans: StringParam,
    //fields: StringParam,
    //signatures: StringParam,
    //planTypes: StringParam,
    //basePlans: StringParam,
  });
  const [loading, setLoading] = useState(true);
  const [paramsLoading, setParamsLoading] = useState(false);

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    async function getTrackers(projectId) {
      const { data: trackers } = await getaGanttIssueTrackers(projectId);
      setTrackers(trackers);
      return trackers;
    }
    async function getUsers(trackerId, projectId) {
      const { data: usersOptions } = await getaGanttIssueTrackersAssignees({
        tracker: trackerId,
        project: projectId,
      });
      return usersOptions;
    }
    if (pageUseParams.projectId) {
      let usersData = {};
      getTrackers(pageUseParams.projectId)
        .then(res => {
          res.forEach((tracker, index, array) => {
            getUsers(tracker.trackerId, pageUseParams.projectId).then(users => {
              usersData[tracker.trackerId] = users;
              if (index === array.length - 1) {
                setRowUsers(usersData);
              }
            });
          });
        })
        .catch(error => console.log(error.response));
    }
  }, [pageUseParams.projectId, setTrackers]);

  const navigate = useNavigate();
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  const [width, setWidth] = useState(50);

  const dragHandler = useCallback(
    e => {
      let w =
        ((e.clientX -
          e.currentTarget.parentElement.parentElement.getBoundingClientRect()
            .left) /
          e.currentTarget.parentElement.parentElement.getBoundingClientRect()
            .width) *
        100;
      if (w > 0 && w !== width) {
        if (w > 99) {
          w = 99;
        }
        setWidth(w);
      }
    },
    [width],
  );
  useEffect(() => {
    let ignore = false;
    async function getPermissions() {
      const { data: response } = await getGanttPermissions(
        pageUseParams.projectId,
      );
      if (!ignore) {
        return response;
      }
    }

    getPermissions()
      .then(res => {
        setPermissions(res);
      })
      .catch(error => handleError(error.response));

    return () => {
      ignore = true;
    };
  }, [pageUseParams.projectId, handleError]);
  useEffect(() => {
    if (socket) {
      let params = {};
      let queryIsSet = true;
      queryIsSet && (params = { ...query });
      if (queryIsSet) {
        setParamsLoading(true);
        socket.emit('getGanttTable', {
          token: accessToken,
          query: params,
          projectId: pageUseParams.projectId,
        });
      }
    }
  }, [accessToken, query, socket, isLoading, pageUseParams.projectId]);

  useEffect(() => {
    let ignore = false;
    async function getGanttFiltersData() {
      const response = await getGantFilters(pageUseParams.projectId);
      if (!ignore) {
        return response;
      }
    }

    getGanttFiltersData()
      .then(res => {
        const { data: paramsData } = res;
        const params = formatFilters(paramsData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
  }, [handleError, pageUseParams.projectId]);

  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams.filters));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParamsGant(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);

  const cellRenderer = useCallback(
    ({ row, column, canEdit }) => {
      let fieldData = row[column.fieldId];
      if (column.fieldType === 'user') {
        fieldData =
          fieldData instanceof Object ? fieldData.valueName : fieldData;
      }
      if (
        fieldData &&
        column.fieldType === 'date' &&
        typeof fieldData === 'string'
      ) {
        fieldData = formatDate(fieldData);
      }
      if (
        fieldData &&
        (column.fieldType === 'cost_fact' || column.fieldType === 'labor_fact')
      ) {
        fieldData = fieldData
          .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
          .replace('.', ',');
      } else if (
        fieldData === 0 &&
        (column.fieldType === 'cost_fact' || column.fieldType === 'labor_fact')
      ) {
        fieldData = '0,00';
      }
      if (
        fieldData &&
        (column.fieldType === 'monthly_distribution' ||
          column.fieldType === 'loading_percent')
      ) {
        fieldData = parseFloat(fieldData)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
          .replace('.', ',');
      } else if (
        fieldData === 0 &&
        (column.fieldType === 'monthly_distribution' ||
          column.fieldType === 'loading_percent')
      ) {
        fieldData = '0,00';
      }
      if (
        column.fieldType === 'monthly_distribution' ||
        column.fieldType === 'loading_percent' ||
        column.fieldType === 'cost_fact' ||
        column.fieldType === 'labor_fact' ||
        column.fieldType === 'date'
      ) {
        return (
          <Box align='left'>
            <Tooltip title={column.fieldName}>
              <Typography
                variant='h5'
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {fieldData}
              </Typography>
            </Tooltip>
          </Box>
        );
      } else if (column.fieldType === 'issues') {
        return (
          <Box align='left'>
            <Tooltip title={column.fieldName}>
              <a
                href={handleGetRedirectUrl({
                  id: row.services.issueId,
                  columnType: 'issue',
                })}
                target='_blank'
              >
                <Typography
                  variant='h5'
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row[column.fieldId]}
                </Typography>
              </a>
            </Tooltip>
          </Box>
        );
      } else if (column.fieldType === 'issueId') {
        return (
          <Box
            align='left'
            sx={{
              overflow: 'hidden',
            }}
          >
            {row.services.isNew || row.kind === 'project' ? (
              ''
            ) : (
              <Tooltip title={column.fieldName}>
                <Typography
                  variant='h5'
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fieldData}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      } else {
        return (
          <Box
            align='left'
            sx={{
              overflow: 'hidden',
            }}
          >
            <Tooltip title={column.fieldName}>
              <Typography
                variant='h5'
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {fieldData}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
    },
    [formatDate],
  );

  const handleField = useCallback((fieldValue, props) => {
    setDisableSaveButton(true);
    const numberFields = ['float', 'int', 'percent'];
    const groupId =
      props.row.kind === 'project' ? props.row.id : props.rowNode.parent;
    let date;
    let newValue = fieldValue;
    if (props.field === 'issue.startDate' || props.field === 'issue.endDate') {
      newValue =
        fieldValue && fieldValue.$d
          ? dayjs(fieldValue.$d).format('YYYY-MM-DD')
          : null;
    }
    props.api.setEditCellValue({
      id: props.id,
      field: props.field,
      value: newValue,
    });
  }, []);

  const handleNumberField = useCallback(
    (value, field) => {
      let reg = new RegExp(/^[0-9,.]*$/);
      field.field === 'issue.duration' && (reg = new RegExp(/^[0-9]*$/));
      if (reg.test(value)) {
        handleField(value, field);
      }
    },
    [handleField],
  );
  const handlePredecessorOptions = useCallback(async (value, props) => {
    let params = {
      row: props.row.id,
      search: value,
      project: props.row.project,
    };
    try {
      const response = await getIssuePredecessors(params);
      if (response) {
        setPredecessors(prevState => {
          prevState[props.row.id] = response.data;
          return prevState;
        });
      }
    } catch (error) {
      console.error('ERROR WITH GET', error);
    }
  }, []);

  const formattedGanttColumns = useCallback(
    columns => {
      const issueIdField = {
        instance: 'issue',
        field: 'id',
        fieldId: 'issue.gantt_id',
        fieldName: 'Id',
        fieldType: 'issueId',
        isChecked: true,
        isCustom: false,
        position: 0,
      };

      let newColumns = [issueIdField].concat(columns);
      newColumns = newColumns.map(column => {
        let type = '';
        let editable = false;
        const numberFields = ['float', 'int', 'percent'];
        switch (column.fieldId) {
          case 'issue.subject':
          case 'issue.assignedTo':
            editable = true;
            break;
          case 'issue.duration':
          case 'issue.loadingPercent':
          case 'issue.estimatedHours':
            editable = true;
            type = 'float';
            break;
          case 'issue.startDate':
          case 'issue.endDate':
            type = 'date';
            editable = true;
            break;
        }
        return {
          field: column.fieldId,
          headerName: column.fieldName,
          renderHeader: props => (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.colDef.headerName}
            </Typography>
          ),
          renderCell: props => {
            const canEdit = props.row?.services?.canEdit;
            const row = props.row;
            //console.log(row);
            return cellRenderer({ row, column, canEdit });
          },
          disableExport: false,
          type: type,
          position: column.position,
          sortable: false,
          editable: editable,
          renderEditCell: props => {
            setDisableSaveButton(true);
            if (props.field === 'issue.predecessor') {
              return (
                <StyledRowAutocomlpete
                  changedStyle={props.row.changed}
                  noOptionsText={getLocalString('labels', 'notFound')}
                  onChange={(event, newValue) => handleField(newValue, props)}
                  disablePortal
                  options={
                    predecessors[props.row.id] ? predecessors[props.row.id] : []
                  }
                  disableClearable
                  renderOption={(props, option) => {
                    return (
                      <MenuItem
                        {...props}
                        key={option.valueId + option.valueName}
                        value={option.valueId}
                      >
                        {option.valueName}
                      </MenuItem>
                    );
                  }}
                  value={props.value ? props.value : ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={option => option.valueName || ''}
                  renderInput={params => (
                    <TextField
                      {...params}
                      onChange={ev =>
                        handlePredecessorOptions(ev.target.value, props)
                      }
                      placeholder={getLocalString('labels', 'select')}
                    />
                  )}
                  popupIcon={<ChevronIcon direction={'down'} />}
                  slots={{
                    popper: props => (
                      <Popper {...props} placement='bottom-start'></Popper>
                    ),
                  }}
                />
              );
            }
            if (props.field === 'issue.assignedTo') {
              return (
                <GanttEditCellUser
                  handleField={handleField}
                  cellProps={props}
                />
              );
            }
            if (props.colDef.type === 'issue_parent') {
              return (
                <StyledRowAutocomlpete
                  shangedStyle={props.row.changed}
                  noOptionsText={getLocalString('labels', 'notFound')}
                  onChange={(event, newValue) => handleField(newValue, props)}
                  disablePortal
                  filterOptions={x => x}
                  options={paentIssueOptions}
                  sx={{ width: 253 }}
                  disableClearable
                  renderOption={(props, option) => {
                    return (
                      <MenuItem
                        {...props}
                        key={option.valueId + option.valueName}
                        value={option.valueId}
                      >
                        {option.valueName}
                      </MenuItem>
                    );
                  }}
                  value={props.value ? props.value : ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={option => option.valueName || ''}
                  renderInput={params => (
                    <TextField
                      {...params}
                      onChange={ev => handleParentIssueChange(ev.target.value)}
                      placeholder={getLocalString('labels', 'select')}
                    />
                  )}
                  popupIcon={<ChevronIcon direction={'down'} />}
                  slots={{
                    popper: props => (
                      <Popper {...props} placement='bottom-start'></Popper>
                    ),
                  }}
                />
              );
            }
            if (props.field === 'issue.tracker2') {
              return (
                <FormControl sx={{ width: '100%' }}>
                  <StyledSelectLabel id='select-label'>
                    {getLocalString('labels', 'select')}
                  </StyledSelectLabel>
                  <StyledRowSelect
                    changedStyle={props.row.changed}
                    native={false}
                    labelId='select-label'
                    label={getLocalString('labels', 'select')}
                    variant='standard'
                    sx={{ width: '100%', border: 'none' }}
                    MenuProps={{
                      slots: { paper: StyledPaperDropdown },
                    }}
                    IconComponent={props => (
                      <ChevronIcon direction={'down'} {...props} />
                    )}
                    value={props.value ? props.value : ''}
                    onChange={e => handleField(e.target.value, props)}
                  >
                    {trackers.map((item, index) => (
                      <MenuItem key={index} value={item.trackerId}>
                        {item.trackerName}
                      </MenuItem>
                    ))}
                  </StyledRowSelect>
                </FormControl>
              );
            }
            if (numberFields.includes(props.colDef.type)) {
              return (
                <StyledRowInput
                  chagedStyle={props.row.changed}
                  autoFocus
                  defaultValue={props.value ? props.value : ''}
                  placeholder={getLocalString('labels', 'input')}
                  variant='standard'
                  value={props.value ? props.value : ''}
                  onChange={e => {
                    handleNumberField(e.target.value, props);
                  }}
                ></StyledRowInput>
              );
            }
            if (props.colDef.type === 'list' || props.colDef.type === 'user') {
              return (
                <FormControl sx={{ width: '100%' }}>
                  <StyledSelectLabel id='select-label'>
                    {getLocalString('labels', 'select')}
                  </StyledSelectLabel>
                  <StyledRowSelect
                    changedStyle={props.row.changed}
                    native={false}
                    labelId='select-label'
                    label={getLocalString('labels', 'select')}
                    variant='standard'
                    sx={{ width: '100%', border: 'none' }}
                    MenuProps={{
                      slots: { paper: StyledPaperDropdown },
                    }}
                    IconComponent={props => (
                      <ChevronIcon direction={'down'} {...props} />
                    )}
                    value={
                      confirmationDocsRows[index][
                        'confirmation.fieldRenewValueId'
                      ]
                        ? confirmationDocsRows[index][
                            'confirmation.fieldRenewValueId'
                          ]
                        : ''
                    }
                    onChange={event => handleField(event, props)}
                  >
                    {props.row.values.map((item, index) => (
                      <MenuItem key={index} value={item.valueId}>
                        {item.valueName}
                      </MenuItem>
                    ))}
                  </StyledRowSelect>
                </FormControl>
              );
            }
            if (props.colDef.type === 'date') {
              return (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={language}
                >
                  <StyledDatePicker
                    changedStyle={props.row.changed}
                    format={'DD.MM.YYYY'}
                    value={props.value ? dayjs(props.value) : null}
                    //autoFocus={true}
                    onChange={value => {
                      if (value && value.isValid()) {
                        handleField(value, props);
                      }
                    }}
                    slots={{
                      leftArrowIcon: () => (
                        <ChevronIcon
                          direction='left'
                          viewBox='0 0 24 24'
                          width={24}
                          height={24}
                        />
                      ),
                      rightArrowIcon: () => (
                        <ChevronIcon
                          direction='right'
                          viewBox='0 0 24 24'
                          width={24}
                          height={24}
                        />
                      ),
                      rightArrowButton: componentProps => (
                        <IconButton disableRipple {...componentProps}>
                          {componentProps.children}
                        </IconButton>
                      ),
                      leftArrowButton: componentProps => (
                        <IconButton disableRipple {...componentProps}>
                          {componentProps.children}
                        </IconButton>
                      ),
                    }}
                  />
                </LocalizationProvider>
              );
            }
          },
          valueFormatter: params => {
            if (
              column.fieldId === 'issue.startDate' ||
              column.fieldId === 'issue.endDate'
            ) {
              if (dayjs(params.value, 'DD.MM.YYYY', true).isValid()) {
                return params.value;
              } else {
                return dayjs(params.value).format('DD.MM.YYYY');
              }
            } else {
              return params?.value;
            }
          },
          cellClassName: () =>
            column.fieldId !== 'issue.gantt_id' && 'tableCell',
        };
      });
      return newColumns.toSpliced(1, 0, {
        field: GRID_TREE_DATA_GROUPING_FIELD,
      });
    },
    [
      language,
      getLocalString,
      cellRenderer,
      predecessors,
      handleField,
      handlePredecessorOptions,
      trackers,
      handleNumberField,
    ],
  );
  const formattedRightSideData = useCallback(
    rows =>
      rows?.map(
        column => {
          let parentForCharts = column.services.projectId;
          let parentForTable = column.services.projectId;
          let user =
            column.signatures.findIndex(
              item => item.fieldId === 'issue.assignedTo',
            ) > 0
              ? column.services.userName
              : '';
          let popupInfo = column.signatures.filter(
            item => item.fieldId !== 'issue.assignedTo',
          );
          if (column.services.hierarchy.length > 2) {
            parentForTable = column.services.parentId;
          }
          let predecessors = column.services.predecessors?.map(predecessor => {
            return {
              sourceId: predecessor.toString(),
              sourceTarget: 'endOfTask',
              ownTarget: 'startOfTask',
            };
          });
          //console.log('formattedRightSideData');
          //console.log(column);
          //console.log(ganttModalData);
          return {
            start: new Date(column.services.startDate),
            end: new Date(column.services.endDate),
            name: column.type === 'issue' ? column.rowName : column.rowName,
            id: column.rowId,
            type: column.type,
            user: user !== null ? user : '',
            popupInfo: popupInfo
              .filter(
                notEmpty =>
                  notEmpty.value !== null && notEmpty.value !== undefined,
              )
              .map(column => {
                let value = column.value;
                switch (column.fieldId) {
                  case 'issue.duration':
                    value = `${column.value} ${getLocalString('labels', 'day')}.`;
                    break;
                  case 'issue.loadingPercent':
                    value = `${column.value} %`;
                    break;
                  case 'issue.estimatedHours':
                    value = `${column.value} ${getLocalString('labels', 'hour')}.`;
                    break;
                  case 'issue.doneRatio':
                    value = `${column.value} %`;
                    break;
                }
                return value;
              })
              .join('; '),
            //...(column.services.predecessors.length > 0 && { parent: column.services.projectId }),
            ...(column.services.predecessors.length > 0 && {
              dependencies: predecessors,
            }),
            ...(column.services.hierarchy.length >= 2 && {
              parent: parentForTable.toString(),
            }),
            /* ...(column.services.hierarchy.length != 1 && {
               dependencies: [
                 {
                   sourceId: parentForCharts,
                   sourceTarget: 'endOfTask',
                   ownTarget: 'startOfTask',
                 },
               ],
             }),*/
            loadingPercent: column.services.loadingPercent,
            progress: column.services.doneRatio,
            duration: column.services.duration,
            isParent: column.services.isParent,
            isDisabled: true,
            hide: false,
            editable: true,
            styles: {
              progressColor: '#ffbb54',
              progressSelectedColor: '#ff9e0d',
            },
          };
        },
        [ganttModalData],
      ),
    [ganttModalData, getLocalString],
  );
  const formattedGanttRows = useCallback(
    rows =>
      rows.reduce((acc, issue) => {
        if (issue.rowType === 'normal') {
          let path = [];

          if (issue.services.parentId) {
            path.push(issue.services.parentId);
            path.push(issue.rowId);
          } else {
            path.push(issue.rowId);
          }
          const newCols = issue.columns.reduce((columnAcc, _, i) => {
            if (issue.columns[i].fieldId == 'issue.subject') {
              issue.columns[i].fieldType = 'issue.subject';
            }
            switch (issue.columns[i].fieldId) {
              case 'issue.startDate':
                let issuestartDate = issue.services.startDate;
                if (issue.type == 'project') {
                  issuestartDate = dayjs(issuestartDate).toISOString();
                }
                return {
                  ...columnAcc,
                  [issue.columns[i].fieldId]: issuestartDate,
                };
              case 'issue.endDate':
                let issueEndDate = issue.services.endDate;
                if (issue.type == 'project') {
                  issueEndDate = dayjs(issueEndDate).toISOString();
                }
                return {
                  ...columnAcc,
                  [issue.columns[i].fieldId]: issueEndDate,
                };
              default:
                return {
                  ...columnAcc,
                  [issue.columns[i].fieldId]: issue.columns[i].value,
                };
            }
          }, {});
          return [
            ...acc,
            {
              id: issue.rowId,
              kind: issue.type,
              project: Number(issue.services.projectId),
              ['issue.gantt_id']: '#' + issue.rowId,
              path: issue.services.hierarchy.map(item => item.toString()),
              ...newCols,
              level: issue.services.level,
              services: issue.services,
              editable: false,
              rowName: issue.rowName,
              rowId: issue.rowId,
              type: issue.type,
              signatures: issue.signatures,
            },
          ];
        }
        return [];
      }, []),

    [],
  );

  /*  useEffect(() => {
    if (ganttModalData) {
      if (ganttModalData.filters) {
        const checkedFilters = ganttModalData.filters.filter(item => item.isChecked);
        const formattedFilters = checkedFilters.map(item => {
          const formattedItem = item;
          item.options = item.values ? item.values : [];
          delete item.values;
          return formattedItem;
        });
        setSelectedFilters(formattedFilters);
      }
      ganttModalData.signatures && setSelectedSignatures(ganttModalData.signatures.filter(item => item.isChecked));
      ganttModalData.planTypes && setSelectedPlanType(ganttModalData.planTypes.find(item => item.isChecked));
    }
  }, [ganttModalData]);*/

  const handleAcceptGanntSave = useCallback(() => {
    setGanttSavingLoader(true);
    if (socket) {
      socket.emit('saveGantt', {
        token: accessToken,
        projectId: Number(pageUseParams.projectId),
        saveSettings: { saveType: gantErrorValue },
      });
    }
  }, [accessToken, gantErrorValue, socket, pageUseParams.projectId]);

  const handleSaveAfterError = useCallback(() => {
    handleAcceptGanntSave();
    setShowSavingError({ state: false, errors: [] });
  }, [handleAcceptGanntSave]);

  useEffect(() => {
    if (socket) {
      let tempData = {};
      socket.on('connect', () => {
        //console.log('Connected');
      });

      socket.on('startGetGanttTable', () => {
        setParamsLoading(true);
        //console.log('startGetGanttTable');
      });
      socket.on('ganttColumns', data => {
        //console.log('ganttColumns', data);
        tempData.ganttColumns = data;
      });
      socket.on('ganttSignatures', data => {
        //console.log('ganttSignatures', data);
        tempData.ganttSignatures = data;
      });
      socket.on('ganttRowsUpdate', data => {
        if (data.length > 0) {
          let leftSideData = formattedGanttRows(data);
          let rightSideData = formattedRightSideData(data);
          /*
          let newleftSideData = ganttTableRowData.map((elem, index) => {
            const indexNewData = leftSideData.findIndex(item => item.id === elem.id);
            if (indexNewData >= 0) {
              elem = leftSideData[indexNewData];
            }
            return elem;
          });
          let newRightSideData = ganttRightSideData.map((elem, index) => {
            const indexNewData = rightSideData.findIndex(item => item.id === elem.id);
            if (indexNewData >= 0) {
              elem = rightSideData[indexNewData];
            }
            return elem;
          });
          setGanttTableRowData(newleftSideData);
          setGanttRightSideData(newRightSideData);
           */
          let newRightSideData = ganttRightSideData.map((elem, index) => {
            const indexNewData = rightSideData.findIndex(
              item => item.id === elem.id,
            );
            if (indexNewData >= 0) {
              elem = rightSideData[indexNewData];
            }
            return elem;
          });
          setGanttRightSideData(newRightSideData);
          apiRef.current.updateRows(leftSideData);
          setDisableSaveButton(false);
          setGanttStartDateErrors(false);
        }
      });
      socket.on('ganttRowUpdate', data => {
        // TO DO only update 1 element in array
        if (data.length > 0) {
          let leftSideData = formattedGanttRows(data);
          let rightSideData = formattedRightSideData(data);
          let newleftSideData = ganttTableRowData.map((elem, index) => {
            const indexNewData = leftSideData.findIndex(
              item => item.id === elem.id,
            );
            if (indexNewData >= 0) {
              elem = leftSideData[indexNewData];
            }
            return elem;
          });
          let newRightSideData = ganttRightSideData.map((elem, index) => {
            const indexNewData = rightSideData.findIndex(
              item => item.id === elem.id,
            );
            if (indexNewData >= 0) {
              elem = rightSideData[indexNewData];
            }
            return elem;
          });
          setGanttTableRowData(newleftSideData);
          setGanttRightSideData(newRightSideData);
          setDisableSaveButton(false);
          setGanttStartDateErrors(false);
        }
      });
      socket.on('endUpdateGanttTable', data => {
        //console.log('endUpdateGanttTable', data);
      });
      socket.on('ganttGroup', data => {
        //console.log('ganttGroup', data);
        tempData.ganttGroup = data;
      });
      socket.on('saveGanttModal', data => {
        //setGanttSavingLoader(true);
      });
      socket.on('endSaveGanttTable', () => {
        setGanttSavingLoader(false);
        setGanttErrorValue('save_all');
        if (ganttRowsErrors.length === 0) {
          //setIsLoading(true);
          setDisableSaveButton(true);
          setGanttStartDateErrors(false);
          socket.emit('getGanttTable', {
            token: accessToken,
            query: query,
            projectId: pageUseParams.projectId,
          });
        }
      });
      socket.on('endSaveGanttModal', data => {
        setGanttSavingLoader(false);
        setDisableSaveButton(false);
      });
      socket.on('checkSaveGanttError', data => {
        setGanttSavingLoader(false);
        if (data.length !== 0) {
          setShowSavingError({ state: true, errors: data });
        } else {
          handleAcceptGanntSave();
        }
      });
      socket.on('ganttMovingIssue', () => {
        //setGanttSavingLoader(true);
      });
      socket.on('endGanttMovingIssue', () => {
        setGanttSavingLoader(false);
      });
      socket.on('ganttUpdateHierarchy', data => {
        let rightSideData = formattedRightSideData(data);
        setGanttRightSideData(rightSideData);
        setGanttTableRowData(formattedGanttRows(data));
        setDisableSaveButton(false);
        setGanttStartDateErrors(false);
      });
      socket.on('endGetGanttTable', () => {
        setGanttTableData(tempData);
        //console.log('endGetGanttTable');
        //console.log(formattedGanttRows(tempData.ganttGroup));
        setGanttRightSideData(formattedRightSideData(tempData.ganttGroup));
        setGanttTableColumnsData(formattedGanttColumns(tempData.ganttColumns));
        setGanttTableRowData(formattedGanttRows(tempData.ganttGroup));
        //setIsLoading(false);
        setLoading(false);
        setGanttStartDateErrors(false);
        setParamsLoading(false);
      });
      socket.on('startDateBeforePredecessorEndDateError', data => {
        setGanttStartDateErrors(true);
      });
      socket.on('ganttSaveErrors', data => {
        setGanttRowsErrors(data.errors);
      });
      socket.on('endDateAfterStartDateError', data => {
        setGanttDateErrors(data);
      });
      socket.on('access_denied_error', () => {
        handleError({ status: '403' });
      });
    }
    return () => {
      if (socket) {
        socket.off('connect');
        socket.off('startGetGanttTable');
        socket.off('ganttColumns');
        socket.off('ganttSignatures');
        socket.off('ganttRowsUpdate');
        socket.off('endUpdateGanttTable');
        socket.off('ganttGroup');
        socket.off('saveGanttModal');
        socket.off('endSaveGanttTable');
        socket.off('endSaveGanttModal');
        socket.off('checkSaveGanttError');
        socket.off('ganttMovingIssue');
        socket.off('endGanttMovingIssue');
        socket.off('ganttUpdateHierarchy');
        socket.off('endGetGanttTable');
        socket.off('ganttSaveErrors');
        socket.off('access_denied_error');
      }
    };
  }, [
    formattedGanttColumns,
    formattedRightSideData,
    formattedGanttRows,
    ganttRightSideData,
    handleAcceptGanntSave,
    rowsToUpdate,
    tempCellValue,
    ganttTableRowData,
    socket,
    apiRef,
    ganttRowsErrors.length,
    handleError,
    accessToken,
    query.filters,
    pageUseParams.projectId,
  ]);

  const handleGanttIssueModalOpen = useCallback(() => {
    setOpenGantIssueModal(true);
  }, []);
  const handleAddBasePlanModalOpen = useCallback(() => {
    setOpenAddBasePlanModal(true);
  }, []);
  const handleTableViewMode = useCallback(
    operation => {
      setGanttTableViewMode(prev => {
        let indexEnum;
        if (operation == 'Up') {
          if (prev == 5) {
            indexEnum = prev;
          } else {
            indexEnum = prev + 1;
          }
        } else if (operation == 'Down') {
          if (prev == 0) {
            indexEnum = prev;
          } else {
            indexEnum = prev - 1;
          }
        }
        setGanttTableViewModeEnum(ViewModeEnum[indexEnum]);
        return indexEnum;
      });
    },
    [setGanttTableViewModeEnum, ViewModeEnum],
  );

  const handleTableViewModeClear = useCallback(() => {
    setGanttTableViewMode(0);
    setGanttTableViewModeEnum(ViewModeEnum['0']);
  }, [setGanttTableViewModeEnum, ViewModeEnum]);

  function CustomGridTreeDataGroupingCell(props) {
    const { id, field, row, rowNode } = props;
    const filteredDescendantCountLookup = useGridSelector(
      apiRef,
      gridFilteredDescendantCountLookupSelector,
    );
    const filteredDescendantCount =
      filteredDescendantCountLookup[rowNode.id] ?? 0;
    const handleClick = () => {
      if (rowNode.type !== 'group') {
        return;
      }
      setFirstLoadGroup(false);
      const newExpandedState = !rowNode.childrenExpanded;
      apiRef.current.setRowChildrenExpansion(id, newExpandedState);
      setGanttHideTask({ id: id, hide: !newExpandedState });
      expansionState.current[id] = newExpandedState;
    };
    return (
      <Box
        sx={{
          marginLeft: rowNode.depth * 3,
          overflow: 'hidden',
        }}
      >
        {filteredDescendantCount > 0 ? (
          <Button
            sx={{
              minWidth: 'auto',
              padding: 0,
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              gap: '5px',
              backgroundColor: 'initial !important',
              color: '#212346',
            }}
            onClick={() => handleClick()}
            tabIndex={-1}
            size='small'
          >
            {rowNode.childrenExpanded ? (
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M4.66675 6.6665L8.00008 9.99984L11.3334 6.6665H4.66675Z'
                  fill='#7174AC'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                fill='none'
                viewBox='0 0 16 16'
              >
                <path
                  fill='#7174AC'
                  d='M6.667 11.333L10 8 6.667 4.667v6.666z'
                ></path>
              </svg>
            )}
            {row.kind === 'project' && (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                fill='none'
                viewBox='0 0 16 16'
              >
                <path
                  fill='#7174AC'
                  d='M13.334 4H8L6.667 2.667h-4c-.733 0-1.327.6-1.327 1.333l-.007 8c0 .733.6 1.333 1.334 1.333h10.666c.734 0 1.334-.6 1.334-1.333V5.333c0-.733-.6-1.333-1.333-1.333zm-4 6.667H4V9.333h5.334v1.334zM12 8H4V6.667h8V8z'
                ></path>
              </svg>
            )}
            <Tooltip title={getLocalString('labels', 'name')}>
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 700,
                }}
              >
                {row['issue.subject']}
              </Typography>
            </Tooltip>
          </Button>
        ) : (
          <Tooltip title={getLocalString('labels', 'name')}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {row.kind === 'project' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '0 4px',
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    fill='none'
                    viewBox='0 0 16 16'
                  >
                    <path
                      fill='#7174AC'
                      d='M13.334 4H8L6.667 2.667h-4c-.733 0-1.327.6-1.327 1.333l-.007 8c0 .733.6 1.333 1.334 1.333h10.666c.734 0 1.334-.6 1.334-1.333V5.333c0-.733-.6-1.333-1.333-1.333zm-4 6.667H4V9.333h5.334v1.334zM12 8H4V6.667h8V8z'
                    ></path>
                  </svg>
                </Box>
              )}
              <Typography
                variant='h5'
                sx={{
                  fontWeight: row.kind === 'project' ? 700 : 400,
                }}
              >
                {row['issue.subject']}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }
  const groupingColDef = {
    headerName: getLocalString('labels', 'name'),
    renderCell: params => <CustomGridTreeDataGroupingCell {...params} />,
    cellClassName: () => 'tableCell tableGroupGanttCell',
    renderHeader: props => (
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
        }}
      >
        {props.colDef.headerName}
      </Typography>
    ),
  };
  const isGroupExpanded = useCallback(
    node => {
      if (firstLoadGroup) {
        expansionState.current[node.id] = true;
        return expansionState.current[node.id];
      } else {
        return !!expansionState.current[node.id];
      }
    },
    [expansionState, firstLoadGroup],
  );

  const handleGanttIssueModalSave = useCallback(
    (data, id) => {
      if (socket) {
        setGanttSavingLoader(true);
        socket.emit('saveGanttModal', {
          token: accessToken,
          groupId: pageUseParams.projectId,
          projectId: Number(pageUseParams.projectId),
          updatedValue: data,
          rowId: id.toString(),
        });
        setOpenGantIssueModal(false);
        setEditIssueData(null);
      }
    },
    [accessToken, socket, pageUseParams.projectId],
  );

  const handleParamsModalClear = useCallback(() => {
    setOpenParamsModal(false);
  }, []);

  const handleEditMenu = useCallback(() => {
    setEditIssueData({
      project: pageUseParams.projectId,
      tracker: Number(selectedRow.services.trackerId),
      id: Number(selectedRow.id),
      name: selectedRow.rowName,
    });
    handleGanttIssueModalOpen();
    setContextMenu(null);
  }, [handleGanttIssueModalOpen, pageUseParams.projectId, selectedRow]);
  const handleDeleteRow = useCallback(() => {
    //console.log('handleDeleteRow');
    socket.emit('deleteGanttRow', {
      token: accessToken,
      rowId: Number(selectedRow.id),
      groupId: selectedRow.project.toString(),
      projectId: selectedRow.project,
    });
    setContextMenu(null);
  }, [selectedRow, accessToken, socket]);
  const handleContextMenu = event => {
    const row = ganttTableRowData.find(
      row =>
        Number(row.id) === Number(event.currentTarget.getAttribute('data-id')),
    );
    event.preventDefault();
    row !== undefined && setSelectedRow(row);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
  };
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };
  const handleContextSubMenu = event => {
    event.preventDefault();
    setContextSubMenu(
      contextSubMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
  };
  const handleCloseContextSubMenu = () => {
    setContextSubMenu(null);
  };

  const handleMovingIssueChange = useCallback(
    async value => {
      let params = {
        search: value,
      };
      selectedRow && (params.row = selectedRow.id);
      pageUseParams.projectId && (params.project = pageUseParams.projectId);
      try {
        const response = await getGanttMovingIssue(params);
        if (response) {
          setMovingIssueOptions(response.data);
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [pageUseParams.projectId, selectedRow],
  );
  const handleCellClick = useCallback(params => {
    //console.log(params);
  }, []);

  const handleSaveGantt = useCallback(() => {
    setGanttSavingLoader(true);
    setGanttRowsErrors([]);
    if (socket) {
      socket.emit('checkGanttIssuesUpdate', {
        token: accessToken,
        projectId: Number(pageUseParams.projectId),
        //saveSettings: { projectId: Number(query.project), saveType: 'save_all' },
      });
    }
  }, [accessToken, pageUseParams.projectId, socket]);

  const handleClearGantt = useCallback(
    async value => {
      setParamsLoading(true);
      try {
        const response = await clearGanttSave(pageUseParams.projectId);
        if (response) {
          socket.emit('getGanttTable', {
            token: accessToken,
            query: query,
            projectId: pageUseParams.projectId,
          });
          setDisableSaveButton(true);
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [accessToken, pageUseParams.projectId, query, socket],
  );
  const handleMovingIssueSelect = useCallback(
    value => {
      if (socket && selectedRow) {
        setGanttSavingLoader(true);
        socket.emit('ganttMovingIssue', {
          token: accessToken,
          projectId: pageUseParams.projectId,
          rowId: selectedRow.id,
          afterId: value.valueId.toString(),
        });
      }
      setContextSubMenu(null);
      setContextMenu(null);
      setMovingIssueOptions([]);
    },
    [accessToken, pageUseParams.projectId, selectedRow, socket],
  );
  React.useEffect(() => {
    if (!isLoading) {
      apiRef.current?.subscribeEvent('rowExpansionChange', node => {
        expansionState.current[node.id] = node.childrenExpanded;
      });
    }
  }, [apiRef, isLoading]);

  const downloadCsv = useCallback(
    e => {
      async function downloadGanttCsv() {
        try {
          setDownloadLoading(true);
          const params = { project: Number(pageUseParams.projectId) };
          if (params) {
            const { data } = await getGantCsv(params);
            const url = window.URL.createObjectURL(
              new Blob(['\ufeff', data], { type: 'text/csv;charset=utf-8;' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              `gantt-${dayjs().format('YYYY-MM-DD')}`,
            );
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            link.remove();
          }
        } catch (e) {
          console.error(e);
        } finally {
          setDownloadLoading(false);
        }
      }
      downloadGanttCsv();
    },
    [pageUseParams.projectId],
  );
  React.useEffect(() => {
    if (ganttStartDateErrors) {
      if (tempCellValue) {
        apiRef.current.updateRows([tempCellValue]);
      }
    }
  }, [tempCellValue, ganttStartDateErrors, apiRef]);

  const handleSelectFilters = useCallback(
    data => {
      const result = selectFilters(data, currentParams);
      setCurrentParams(result);
      setQuery(
        paramsToQuery({
          filters: {
            ...result.filters,
            selectedItems: result.filters.selectedItems.filter(
              item => item.fieldType !== 'base_plan',
            ),
          },
          basePlans: {
            selectedItems: result.filters.selectedItems.filter(
              item => item.fieldType === 'base_plan',
            ),
          },
        }),
      );
      //setParamsLoading(true);
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    async params => {
      function formatValue(value) {
        return value.map(item => {
          return { instance: item.instance, field: item.field };
        });
      }
      let data = {};
      if (
        params.filters &&
        params.filters.selectedItems &&
        params.filters.selectedItems.filter(
          item => item.fieldType !== 'base_plan',
        ).length > 0
      ) {
        data.filters = formatValue(
          params.filters.selectedItems.filter(
            item => item.fieldType !== 'base_plan',
          ),
        );
      } else {
        data.filters = [];
      }
      params.fields.selectedItems.selectedFields.length > 0 &&
        (data.fields = formatValue(params.fields.selectedItems.selectedFields));
      if (
        params.signatures &&
        params.signatures.selectedItems &&
        params.signatures.selectedItems.length > 0
      ) {
        data.signatures = formatValue(params.signatures.selectedItems);
      } else {
        data.signatures = [];
      }
      params.planTypes.selectedItems &&
        (data.planType = Number(params.planTypes.selectedItems.valueId));
      try {
        const { data: response } = await saveGanttSettings(
          pageUseParams.projectId,
          data,
        );
        if (response) {
          setCurrentParams(params);
          setOpenParamsModal(false);
          setParamsLoading(true);
          socket.emit('getGanttTable', {
            token: accessToken,
            query: query,
            projectId: pageUseParams.projectId,
          });
        }
      } catch (error) {
        handleError(error.response);
      }
    },
    [accessToken, handleError, pageUseParams.projectId, query, socket],
  );

  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.ADD:
          setOpenGantIssueModal(true);
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
        case ButtonsTypes.DOWNLOAD:
          downloadCsv();
          break;
        case ButtonsTypes.ADD_BASE_PLAN:
          setOpenAddBasePlanModal(true);
          break;
        case ButtonsTypes.SAVE:
          handleSaveGantt();
          break;
        case ButtonsTypes.CANCEL:
          handleClearGantt();
      }
    },
    [downloadCsv, handleSaveGantt, handleClearGantt],
  );

  const handleGanttIssueModalClose = useCallback(() => {
    setOpenGantIssueModal(false);
    setEditIssueData(null);
  }, []);
  const handleGanttIssueRelatedModalOpen = useCallback(() => {
    setOpenGantIssueRelatedModal(true);
  }, []);
  const handleGanttIssueRelatedModalClose = useCallback(() => {
    setOpenGantIssueRelatedModal(false);
  }, []);
  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);
  const handleAddBasePlanModalClose = useCallback(() => {
    setOpenAddBasePlanModal(false);
  }, []);

  const handleBasePlanSave = useCallback(() => {
    setOpenAddBasePlanModal(false);
    if (socket) {
      socket.emit('getGanttTable', {
        token: accessToken,
        query: query,
        projectId: pageUseParams.projectId,
      });
    }
  }, [socket, pageUseParams.projectId, query]);

  return (
    <>
      {socket && (
        <GanttIssueModal
          socket={socket}
          token={accessToken}
          data={editIssueData}
          open={openGantIssueModal}
          handleClose={handleGanttIssueModalClose}
          handleSave={handleGanttIssueModalSave}
        ></GanttIssueModal>
      )}
      <GanttIssueRelatedModal
        open={openGantIssueRelatedModal}
        handleClose={handleGanttIssueRelatedModalClose}
        //handleSave={handleGanttIssueModalSave}
      ></GanttIssueRelatedModal>
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      {featureFlags.BASE_PLANS && (
        <GanttAddBasePlanModal
          open={openAddBasePlanModal}
          handleSave={handleBasePlanSave}
          handleClose={handleAddBasePlanModalClose}
        ></GanttAddBasePlanModal>
      )}
      <CommonPage
        disableSave={disableSaveButton}
        loading={loading}
        canCreate={true}
        buttonEvent={handleButtonEvent}
        downloadLoading={downloadLoading}
        component={'GanttContainer'}
        canAddBasePlan={permissions && permissions.canSaveBasePlan}
      >
        <Grid
          container
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ marginBottom: '20px', marginTop: '10px' }}
        >
          <Grid size={'grow'}>
            {currentParams &&
              currentParams.filters?.selectedItems?.length > 0 && (
                <GridFiltersPanelNew
                  data={currentParams.filters.selectedItems}
                  handleCloseFilter={handleSelectFilters}
                ></GridFiltersPanelNew>
              )}
          </Grid>
          <Grid>
            <Grid>
              <IconButton
                onClick={() => handleTableViewMode('Down')}
                sx={{ padding: '0', cursor: 'pointer' }}
              >
                <ZoomInIcon />
              </IconButton>
              {ganttTableViewModeEnum != 'Year' ? (
                <IconButton
                  onClick={() => handleTableViewMode('Up')}
                  sx={{ padding: '0', cursor: 'pointer', marginLeft: '4px' }}
                >
                  <ZoomOutIcon />
                </IconButton>
              ) : null}
              {ganttTableViewModeEnum == 'Year' ? (
                <IconButton
                  onClick={() => handleTableViewModeClear()}
                  sx={{ padding: '0', cursor: 'pointer', marginLeft: '4px' }}
                >
                  <ZoomCancelIcon />
                </IconButton>
              ) : null}
            </Grid>
          </Grid>
        </Grid>

        {showSavingError.state && (
          <Grid
            container
            direction='column'
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography
              variant='h3'
              sx={{
                fontWeight: 600,
                color: '#E03737',
              }}
            >
              {getLocalString('errors', 'issueUpdated')}
            </Typography>
            {showSavingError.errors.length > 0 &&
              showSavingError.errors.map((issue, index) => {
                const values = Object.entries(issue.updatedValues);
                return values
                  .filter(item => item[1])
                  .map(value => {
                    return (
                      <Box>
                        <Typography
                          variant='h4'
                          sx={{
                            color: '#212346',
                          }}
                        >
                          <Box
                            component='span'
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {`${getLocalString('GanttContainer', 'errors', value[0])}`}
                          </Box>
                          <Box component='span'>{` ${getLocalString('GanttContainer', 'errors', 'text')} `}</Box>
                          <Box
                            component='span'
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {issue.issueName}
                          </Box>
                          <Box component='span'>{` ${getLocalString('GanttContainer', 'errors', 'changeFrom')} `}</Box>
                          <Box
                            component='span'
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {value[1].old}
                          </Box>
                          <Box component='span'>{` ${getLocalString('GanttContainer', 'errors', 'changeTo')} `}</Box>
                          <Box
                            component='span'
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {value[1].new}
                          </Box>
                        </Typography>
                      </Box>
                    );
                  });
              })}
            <Grid
              container
              wrap='nowrap'
              direction='column'
              spacing={0}
              sx={{
                marginLeft: 4,
              }}
            >
              <RadioGroup
                value={gantErrorValue}
                onChange={e => setGanttErrorValue(e.target.value)}
                defaultValue={'save_all'}
              >
                <FormControlLabel
                  value='save_all'
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('messages', 'saveMyChanges')}
                />
                <FormControlLabel
                  value='cancel_changes'
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('messages', 'clearMyChanges')}
                />
              </RadioGroup>
              <Grid>
                <Button
                  variant='defaultBluePrimary'
                  disableRipple
                  onClick={handleSaveAfterError}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {getLocalString('labels', 'accept')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
        {ganttRowsErrors.length > 0 &&
          ganttRowsErrors.map((item, index) => (
            <Box
              sx={{
                marginTop: index > 0 ? '10px' : 0,
              }}
            >
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 600,
                  color: '#E03737',
                }}
              >
                {`#${item.id}: ${item.message}`}
              </Typography>
            </Box>
          ))}
        {ganttDateErrors && (
          <Box>
            <Typography
              variant='h3'
              sx={{
                color: '#E03737',
              }}
            >
              {`#${ganttDateErrors.rowId}: ${getLocalString('GanttContainer', 'errors', 'dateError')}`}
            </Typography>
          </Box>
        )}
        {ganttStartDateErrors && (
          <Box>
            <Typography
              variant='h3'
              sx={{
                color: '#E03737',
              }}
            >
              {getLocalString('GanttContainer', 'errors', 'startDateError')}
            </Typography>
          </Box>
        )}
        <Grid
          container
          sx={{
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: '16px',
          }}
        >
          {paramsLoading && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                zIndex: 99,
                position: 'absolute',
              }}
            >
              <CircularProgress color='secondary' />
            </Grid>
          )}
          <StyledGanttHolder
            container
            className={'flex grow overflow-clip relative'}
          >
            <Box style={{ width: `${width}%` }}>
              <StyledDataGridTableRoot
                fixedHeaderStyle={true}
                localeText={{
                  noRowsLabel: getLocalString('labels', 'noData'),
                }}
                isGroupExpandedByDefault={isGroupExpanded}
                onCellClick={props => handleCellClick(props)}
                onCellEditStop={(params, event) => {
                  const { id, field } = params;
                  setTimeout(() => {
                    params.value = apiRef.current.getCellValue(id, field);
                    if (socket) {
                      const numberFields = ['float', 'int', 'percent'];
                      const groupId =
                        params.row.kind === 'project'
                          ? params.row.id
                          : params.rowNode.parent;
                      let date;
                      let fieldValue = params.value;
                      let newValue = fieldValue;
                      if (
                        field === 'issue.startDate' ||
                        field === 'issue.endDate'
                      ) {
                        newValue =
                          fieldValue && fieldValue.$d
                            ? dayjs(fieldValue.$d).format('YYYY-MM-DD')
                            : '';
                      }
                      setDisableSaveButton(false);
                      switch (field) {
                        case 'issue.subject':
                          socket.emit('updateGanttNameAndUser', {
                            token: accessToken,
                            rowId: id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              name: fieldValue,
                              userId: null,
                            },
                          });
                          break;
                        case 'issue.assignedTo':
                          socket.emit('updateGanttNameAndUser', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              name: params.row['issue.subject'],
                              userId: Number(newValue.valueId),
                            },
                          });
                          break;
                        case 'issue.duration':
                          socket.emit('updateGanttDuration', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              duration: Number(fieldValue.replace(',', '.')),
                            },
                          });
                          break;
                        case 'issue.loadingPercent':
                          socket.emit('updateGanttLoadingPercent', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              loadingPercent: Number(
                                fieldValue.replace(',', '.'),
                              ),
                            },
                          });
                          break;
                        case 'issue.estimatedHours':
                          socket.emit('updateGanttEstimatedHours', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              estimatedHours: Number(
                                fieldValue.replace(',', '.'),
                              ),
                            },
                          });
                          break;
                        case 'issue.startDate':
                          setGanttDateErrors(null);
                          setTempCellValue(params.row);
                          socket.emit('updateGanttStartDate', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              updateDate: fieldValue,
                            },
                          });
                          break;
                        case 'issue.endDate':
                          setGanttDateErrors(null);
                          setTempCellValue(params.row);
                          socket.emit('updateGanttEndDate', {
                            token: accessToken,
                            rowId: params.id,
                            groupId: params.row.project.toString(),
                            projectId: params.row.project,
                            updatedValue: {
                              updateDate: fieldValue,
                            },
                          });
                          break;
                      }
                    }
                  }, 100);
                }}
                ref={dataGridProRef}
                rows={ganttTableRowData}
                columns={ganttTableColumnsData}
                apiRef={apiRef}
                loading={ganttTableRowData.length === 0}
                rowHeight={40}
                //checkboxSelection
                groupingColDef={groupingColDef}
                hideFooter
                getTreeDataPath={row => row['path']}
                disableColumnFilter
                disableRowSelectionOnClick
                disableColumnMenu
                treeData
                isCellEditable={params => params.row.kind != 'project'}
                getRowClassName={params =>
                  params.row.services.isError && 'errorRow'
                }
                sx={{
                  '& .MuiDataGrid-row': {
                    marginTop: 1,
                    marginBottom: 1,
                    margin: 0,
                  },
                  '& .coloured': { textAlign: 'center', color: '#7181AD' },
                  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '&.MuiDataGrid-root': {
                    '& .MuiDataGrid-main': {
                      '& .MuiDataGrid-virtualScroller': {
                        paddingTop: '0',
                        '& .MuiDataGrid-container--top': {
                          height: '55px',
                        },
                      },
                    },
                  },
                }}
                slotProps={{
                  row: {
                    onContextMenu: handleContextMenu,
                    style: { cursor: 'context-menu' },
                  },
                }}
              />
              <Menu
                sx={{
                  '& .MuiPaper-root': {
                    border: '1px solid #E4E4EF',
                    boxShadow: '0px 5px 10px -8px #A0A1AA66',
                    borderRadius: '16px',
                  },
                  '& .MuiList-root': { padding: 0 },
                }}
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference='anchorPosition'
                anchorPosition={
                  contextMenu !== null
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
                }
                slotProps={{
                  root: {
                    onContextMenu: event => {
                      event.preventDefault();
                      handleClose();
                    },
                  },
                }}
              >
                {selectedRow && selectedRow.services.canEdit && (
                  <MenuItem
                    sx={{
                      fontSize: '12px',
                      color: '#212346',
                      lineHeight: '20px',
                    }}
                    onClick={handleEditMenu}
                  >
                    {getLocalString('labels', 'edit')}
                  </MenuItem>
                )}
                {selectedRow && selectedRow.services.isNew && (
                  <MenuItem
                    sx={{
                      fontSize: '12px',
                      color: '#212346',
                      lineHeight: '20px',
                    }}
                    onClick={handleDeleteRow}
                  >
                    {getLocalString('labels', 'delete')}
                  </MenuItem>
                )}
                <MenuItem
                  sx={{
                    fontSize: '12px',
                    color: '#212346',
                    lineHeight: '20px',
                  }}
                  onClick={handleContextSubMenu}
                >
                  {getLocalString('labels', 'move')}
                </MenuItem>
              </Menu>
              <Menu
                sx={{
                  '& .MuiPaper-root': {
                    border: '1px solid #E4E4EF',
                    boxShadow: '0px 5px 10px -8px #A0A1AA66',
                    borderRadius: '16px',
                  },
                  '& .MuiList-root': { padding: 0 },
                }}
                open={contextSubMenu !== null}
                onClose={handleCloseContextSubMenu}
                anchorReference='anchorPosition'
                anchorPosition={
                  contextSubMenu !== null
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
                }
                slotProps={{
                  root: {
                    onContextMenu: event => {
                      event.preventDefault();
                      handleClose();
                    },
                  },
                }}
              >
                <MenuItem
                  sx={{
                    fontSize: '12px',
                    color: '#212346',
                    lineHeight: '20px',
                    '&:hover': { backgroundColor: '#fff' },
                    '&.Mui-focusVisible': { backgroundColor: '#fff' },
                  }}
                >
                  <Grid container direction={'column'}>
                    <Grid>{getLocalString('labels', 'after')}</Grid>
                    <Grid>
                      <StyledFilterSelect
                        noOptionsText={getLocalString('labels', 'notFound')}
                        filterOptions={x => x}
                        onChange={(event, newValue) =>
                          handleMovingIssueSelect(newValue)
                        }
                        disablePortal
                        options={movingIssueOptions}
                        sx={{ width: 253 }}
                        disableClearable
                        renderOption={(props, option) => {
                          return (
                            <MenuItem
                              {...props}
                              key={option.valueId + option.valueName}
                              value={option.valueId}
                            >
                              {option.valueName}
                            </MenuItem>
                          );
                        }}
                        getOptionLabel={option => option.valueName || ''}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        renderInput={params => (
                          <TextField
                            {...params}
                            onChange={ev =>
                              handleMovingIssueChange(ev.target.value)
                            }
                            placeholder={getLocalString('labels', 'select')}
                          />
                        )}
                        popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                        slots={{
                          popper: props => (
                            <Popper
                              {...props}
                              sx={{ transform: 'none!important' }}
                              popperOptions={{ strategy: 'relative' }}
                              placement='bottom-start'
                            ></Popper>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </MenuItem>
              </Menu>
              <div
                draggable
                onDrag={dragHandler}
                className='flex justify-center items-center p-1 h-full bg-slate-800 cursor-col-resize'
                style={{
                  position: 'absolute',
                  left: `${width}%`,
                  top: 0,
                  height: '100%',
                  width: '3px',
                  background: '#e4e4ef',
                  cursor: 'col-resize',
                  zIndex: 2,
                }}
              >
                <div className='w-1 border-x border-white h-1/6' />
              </div>
            </Box>

            <Box style={{ width: `${100 - width}%`, zIndex: 1 }}>
              <Gantt
                ganttTableShowChilds={ganttTableShowChilds}
                ganttHideTask={ganttHideTask}
                setGanttTableShowChilds={setGanttTableShowChilds}
                setGanttHideTask={setGanttHideTask}
                comparisonLevels={1}
                columnsTable={ganttTableColumnsData}
                rowsTable={ganttTableRowData}
                tasks={ganttRightSideData}
                viewMode={ganttTableViewModeEnum}
              />
            </Box>
          </StyledGanttHolder>
        </Grid>
      </CommonPage>
    </>
  );
};

export default GanttContainer;
